import { AutomationWizardStepProps } from "../../AutomationsUtils";
import { useForm, FormProvider, Controller, useWatch } from "react-hook-form";
import { Link } from "react-router-dom";
import {
  ticketEndpointTypeEnum,
  ticketManagersEnum,
} from "@SeemplicityDev/ticket-master";
import FormWizardFormWrapper from "../../../Forms/FormWizard/FormWizardFormWrapper";
import PageFormWidget from "../../../Forms/FormWizard/PageFormRenderer/PageFormWidget/PageFormWidget";
import "./NotificationSystemStep.scss";
import { useState } from "react";
import {
  TextButton,
  TextInput,
  REQUIRED_ERROR_MESSAGE,
} from "@SeemplicityDev/shared";
import PageFormButtons from "../../../Forms/FormWizard/PageFormRenderer/PageFormButtons/PageFormButtons";
import IntegrationInput from "../IntegrationInput/IntegrationInput";

export const REPORT_SUBJECT_MAX_LENGTH = 255;

export const REPORT_SUBJECT_DATE_HELP_TEXT =
  "Every report includes the date it was sent (UTC).";

export const REPORT_SUBJECT_TOKEN_HELP_TEXT =
  "Add {{date}} to choose where that date appears; otherwise it goes at the end.";

export const EMAIL_SUBJECT_LABEL = "Email Subject";
export const NOTIFICATION_TITLE_LABEL = "Notification Title";

export const getReportSubjectLabel = (integrationType?: string): string =>
  integrationType === ticketManagersEnum.EMAIL
    ? EMAIL_SUBJECT_LABEL
    : NOTIFICATION_TITLE_LABEL;

export const REPORT_SUBJECT_HELP_TEXT = `${REPORT_SUBJECT_DATE_HELP_TEXT}\n${REPORT_SUBJECT_TOKEN_HELP_TEXT}`;

export const getCustomReportSubject = (
  subject: string | undefined,
  reportSubjectDefault: string
): string | undefined => {
  const customSubject = subject?.trim();
  return customSubject && customSubject !== reportSubjectDefault
    ? customSubject
    : undefined;
};

const NotificationSystemStep: React.FC<
  AutomationWizardStepProps & {
    fieldMappingId?: string;
    reportSubjectDefault: string;
  }
> = ({
  defaultValues,
  onValidate = () => {},
  fieldMappingId,
  isSummary = false,
  reportSubjectDefault,
}) => {
  const [disableNextStep, setDisableNextStep] = useState<boolean>(true);
  const [isloading, setisLoading] = useState<boolean>(true);

  const methods = useForm({
    mode: "all",
    values: defaultValues,
  });
  const { handleSubmit, getValues, control } = methods;

  const integrationType = useWatch({ control, name: "integrationType" });

  const getEmptyStateAlertContent = () => {
    return (
      <div className="one-report-manager-alert">
        <div>
          Integration with other notification tools, such as Slack or Microsoft
          Teams, is available to support alternative report delivery methods.
        </div>
        <Link to="/settings/integrations/notificationSystems" target="_blank">
          <TextButton
            label="Connect Now"
            type={`button`}
            data-testid={`connect-now-notificiation`}
            buttonStyle="textLink"
          />
        </Link>
      </div>
    );
  };

  return (
    <FormProvider {...methods}>
      <FormWizardFormWrapper
        handleSubmit={handleSubmit}
        onSubmit={onValidate}
        getValues={getValues}
      >
        <PageFormWidget
          isSummary={isSummary}
          bodyClassName={isloading ? "notifications-step-widget" : ""}
        >
          <div
            className={`notification-system-step-content ${
              isSummary ? "is-summary" : ""
            }`}
          >
            <IntegrationInput
              type={ticketEndpointTypeEnum.REPORT}
              isSummary={isSummary}
              defaultValues={defaultValues}
              fieldMappingId={fieldMappingId}
              onLoaded={() => setisLoading(false)}
              onConfigurationChange={() => setDisableNextStep(true)}
              onFinalPhase={(isFinal: boolean | undefined) =>
                setDisableNextStep(!isFinal)
              }
              emptyStateAlertContent={getEmptyStateAlertContent()}
            />
            <Controller
              name="report_subject"
              control={control}
              defaultValue={reportSubjectDefault}
              rules={{ required: REQUIRED_ERROR_MESSAGE }}
              render={({
                field: { value, onChange, onBlur },
                fieldState: { error },
              }) => (
                <TextInput
                  wrapperClassName="report-subject-container"
                  label={getReportSubjectLabel(integrationType)}
                  viewMode={isSummary}
                  labelTop
                  required
                  data-testid="report-subject-input"
                  name="report_subject"
                  max={REPORT_SUBJECT_MAX_LENGTH}
                  placeholder={reportSubjectDefault}
                  error={error}
                  onChange={onChange}
                  value={value}
                  onBlur={onBlur}
                  description={REPORT_SUBJECT_HELP_TEXT}
                />
              )}
            />
          </div>
        </PageFormWidget>
        {!isSummary && (
          <PageFormButtons
            data-testid="notification-system-step-buttons"
            nextButtonDisabled={disableNextStep}
          />
        )}
      </FormWizardFormWrapper>
    </FormProvider>
  );
};

export default NotificationSystemStep;
